import { Box } from '@bechtel/gds';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title } from 'chart.js';
import { has, isNull, map } from 'lodash';
import { FC } from 'react';
import { Bar } from 'react-chartjs-2';
import { useTheme } from 'styled-components';
import { StatDict, Tile } from '~/components';
import { BarChartDataProps } from './types';

const options = {
  indexAxis: 'y' as const,
  responsive: true,
  plugins: {
    legend: {
      display: false, // Show the custom legend
    },
    tooltip: {
      enabled: false, // <-- this option disables tooltips
    },
  },
  scales: {
    x: {
      display: false,
    },
    y: {
      display: false,
    },
  },
};

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Legend);

interface BarChartProps {
  selectedLabel: string;
  barChartData: BarChartDataProps;
  onSelectionChange: (labelKey: string) => void;
  statDict: StatDict;
}
export const BarChart: FC<BarChartProps> = ({ selectedLabel, barChartData, onSelectionChange, statDict }) => {
  const theme = useTheme();
  const { total, withoutCablesCount, withRevCardCount, holdCount, cablesRouted, terminated } = barChartData;
  const labels: string[] = [''];

  const data = {
    labels,
    datasets: [
      {
        key: statDict.total.key,
        label: statDict.total.label,
        data: [total],
        backgroundColor: `${theme.colors.blue[400]}`,
        clickable: false,
      },
      has(barChartData, 'withoutCablesCount')
        ? {
            key: statDict?.withoutCables?.key,
            label: statDict.withoutCables?.label,
            data: [withoutCablesCount],
            backgroundColor: `${theme.colors.orange[500]}`,
          }
        : null,
      has(barChartData, 'cablesRouted')
        ? {
            key: statDict.cablesRouted.key,
            label: statDict.cablesRouted.label,
            data: [cablesRouted],
            backgroundColor: `${theme.colors.orange[500]}`,
          }
        : null,
      has(barChartData, 'terminated')
        ? {
            key: statDict.terminated.key,
            label: statDict.terminated.label,
            data: [terminated],
            backgroundColor: `${theme.colors.green[400]}`,
          }
        : null,
      has(barChartData, 'withRevCardCount')
        ? {
            key: statDict?.withRevCards?.key,
            label: statDict.withRevCards.label,
            data: [withRevCardCount],
            backgroundColor: `${theme.colors.purple[500]}`,
          }
        : null,
      {
        key: statDict.onHold.key,
        label: statDict.onHold.label,
        data: [holdCount],
        backgroundColor: `${theme.colors.red[500]}`,
      },
    ].filter((dataset) => dataset !== null),
  };

  return (
    <>
      {total > 0 && (
        <Box>
          <Bar options={options} data={data} />
        </Box>
      )}
      {map(data.datasets, (item) => {
        if (!isNull(item)) {
          return (
            <Tile
              clickable={item?.clickable}
              key={item?.key}
              selected={item?.key === selectedLabel}
              clickHandler={(labelKey) => onSelectionChange(labelKey)}
              label={item?.label}
              value={item?.data[0]}
              legendColor={item?.backgroundColor}
              labelKey={item?.key}
            />
          );
        }
      })}
    </>
  );
};
